import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Baby, Heart, CheckCircle, Clock, Book, ShoppingBag, FileText, AlertCircle, XCircle, ArrowRight, Star, ShieldCheck, X } from 'lucide-react';
// --- Theme Constants ---
const COLORS = {
  cream: '#FDF8F3',
  terracotta: '#E8A87C',
  forest: '#2D4A3E',
  blush: '#F5D5C8',
  white: '#FFFFFF'
};
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
// --- Components ---
const Button = ({
  children,
  className = '',
  variant = 'primary',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary';
}) => {
  const baseStyles = 'inline-flex items-center justify-center px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl active:scale-95';
  const variants = {
    primary: 'bg-[#E8A87C] text-[#2D4A3E] hover:bg-[#E09565]',
    secondary: 'bg-[#2D4A3E] text-[#FDF8F3] hover:bg-[#1F352C]'
  };
  return <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props} data-id="element-0">
      {children}
    </button>;
};
const Section = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <section id={id} className={`py-16 md:py-24 px-4 md:px-8 ${className}`} data-id="element-1">
    <div className="max-w-6xl mx-auto" data-id="element-2">{children}</div>
  </section>;
const Hero = () => {
  return <section className="relative min-h-screen flex items-center pt-20 pb-16 px-4 md:px-8 overflow-hidden bg-[#FDF8F3]" data-id="element-3">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[#F5D5C8] opacity-20 rounded-l-full blur-3xl -z-10" data-id="element-4" />

      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-5">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-6">
          <motion.div variants={fadeInUp} className="inline-flex items-center space-x-2 bg-[#2D4A3E]/10 px-4 py-2 rounded-full text-[#2D4A3E] font-medium text-sm" data-id="element-7">
            <Moon size={16} data-id="element-8" />
            <span data-id="element-9">Son las 2:13 a.m.</span>
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-[#2D4A3E]" data-id="element-10">
            Alivio en 7 Días:{' '}
            <span className="text-[#E8A87C] italic" data-id="element-11">sin dietas extremas.</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-xl text-[#2D4A3E]/80 leading-relaxed max-w-lg" data-id="element-12">
            Un protocolo suave + menú exprés + diario simple para recuperar
            control en 1 semana. Deja de adivinar qué le cae mal a tu bebé.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-13">
            <Button onClick={() => document.getElementById('pricing')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-14">
              Descarga tu guía ahora - $6.86
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-15" />
            </Button>
          </motion.div>

          <motion.div variants={fadeInUp} className="flex items-center gap-4 text-sm text-[#2D4A3E]/70 pt-2" data-id="element-16">
            <div className="flex -space-x-2" data-id="element-17">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-[#E8A87C]/30 border-2 border-[#FDF8F3] flex items-center justify-center text-xs font-bold" data-id="element-18">
                  <Baby size={14} data-id="element-19" />
                </div>)}
            </div>
            <p data-id="element-20">Más de 500 mamás han recuperado el sueño</p>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        rotate: 2
      }} animate={{
        opacity: 1,
        scale: 1,
        rotate: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative flex justify-center" data-id="element-21">
          <div className="relative z-10 w-full max-w-md" data-id="element-22">
            <div className="absolute inset-0 bg-[#E8A87C] rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-23"></div>
            <img src="https://cdn.magicpatterns.com/uploads/fXzbaBMHTsNNjCRFNG46WM/alimentacion_anticolicos_para_mama_en_lactancia.png" alt="Alimentación Anticólicos Ebook Cover" className="relative w-full h-auto rounded-2xl shadow-2xl transform transition-transform duration-500 hover:scale-[1.02]" data-id="element-24" />
          </div>
        </motion.div>
      </div>
    </section>;
};
const Problem = () => {
  const painPoints = ['Estás lactando y tu bebé tiene episodios de gases/cólicos que te dejan sin aire', "Sientes que tu día gira alrededor de '¿qué le cayó mal?'", 'Ya intentaste remedios sueltos y nada se sostiene', 'Quieres una guía en español, clara, sin regaños, hecha para la vida real en USA'];
  return <Section className="bg-white" data-id="element-25">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} className="grid md:grid-cols-2 gap-16 items-center" data-id="element-26">
        <div className="space-y-8" data-id="element-27">
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-[#2D4A3E]" data-id="element-28">
            ¿Para quién es esto?
          </motion.h2>
          <motion.div variants={fadeInUp} className="space-y-6" data-id="element-29">
            {painPoints.map((point, idx) => <div key={idx} className="flex items-start gap-4" data-id="element-30">
                <div className="mt-1 bg-[#F5D5C8] p-2 rounded-full text-[#2D4A3E]" data-id="element-31">
                  <CheckCircle size={20} data-id="element-32" />
                </div>
                <p className="text-lg text-[#2D4A3E]/80" data-id="element-33">{point}</p>
              </div>)}
          </motion.div>
        </div>

        <motion.div variants={fadeInUp} className="bg-[#FDF8F3] p-8 md:p-12 rounded-3xl border border-[#F5D5C8]" data-id="element-34">
          <h3 className="text-2xl font-bold text-[#2D4A3E] mb-6 font-serif" data-id="element-35">
            El problema real...
          </h3>
          <p className="text-lg text-[#2D4A3E]/80 mb-6" data-id="element-36">
            El problema no es solo el llanto. Es lo que el llanto te hace a ti:
          </p>
          <ul className="space-y-4" data-id="element-37">
            <li className="flex items-center gap-3 text-[#2D4A3E]/90" data-id="element-38">
              <XCircle className="text-[#E8A87C]" size={20} data-id="element-39" /> Te quita el sueño
            </li>
            <li className="flex items-center gap-3 text-[#2D4A3E]/90" data-id="element-40">
              <XCircle className="text-[#E8A87C]" size={20} data-id="element-41" /> Te llena de culpa
            </li>
            <li className="flex items-center gap-3 text-[#2D4A3E]/90" data-id="element-42">
              <XCircle className="text-[#E8A87C]" size={20} data-id="element-43" /> Te hace comer
              cualquier cosa a cualquier hora
            </li>
          </ul>
          <p className="mt-8 text-[#2D4A3E] italic font-medium border-l-4 border-[#E8A87C] pl-4" data-id="element-44">
            "Y cuando por fin alguien te dice 'cambia tu alimentación', nadie te
            dice cómo hacerlo sin caer en locura."
          </p>
        </motion.div>
      </motion.div>
    </Section>;
};
const FailedAttempts = () => {
  return <Section className="bg-[#2D4A3E] text-[#FDF8F3]" data-id="element-45">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-16" data-id="element-46">
        <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-47">
          Por qué lo que ya intentó falló
        </h2>
        <p className="text-[#FDF8F3]/80 text-lg" data-id="element-48">
          Porque "comer saludable" no basta. "Saludable" no significa "suave"
          para tu sistema hoy.
        </p>
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-49">
        {[{
        icon: <Baby size={32} data-id="element-50" />,
        title: 'Sin un sistema',
        desc: "Probaste 'un remedio' a la vez (gotas, masajes, posición) pero sin estructura."
      }, {
        icon: <AlertCircle size={32} data-id="element-51" />,
        title: 'Sin energía',
        desc: 'Eliminaste alimentos al azar y terminaste agotada sin saber qué causó qué.'
      }, {
        icon: <Clock size={32} data-id="element-52" />,
        title: 'Sin plan claro',
        desc: 'Nadie te dio un plan corto y repetible para recuperar control en una semana.'
      }].map((item, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-[#FDF8F3]/5 p-8 rounded-2xl border border-[#FDF8F3]/10 hover:bg-[#FDF8F3]/10 transition-colors" data-id="element-53">
            <div className="text-[#E8A87C] mb-4" data-id="element-54">{item.icon}</div>
            <h3 className="text-xl font-bold mb-3" data-id="element-55">{item.title}</h3>
            <p className="text-[#FDF8F3]/70" data-id="element-56">{item.desc}</p>
          </motion.div>)}
      </motion.div>
    </Section>;
};
const Solution = () => {
  const features = [{
    title: 'Mapa Anticólicos',
    desc: 'Alimentos por probabilidad (no por miedo).',
    icon: <Book size={24} data-id="element-57" />
  }, {
    title: 'Reset Suave (48h)',
    desc: 'Para bajar el caos sin quedarte sin comer.',
    icon: <Moon size={24} data-id="element-58" />
  }, {
    title: 'Diario Simple',
    desc: '3 datos por toma (sin obsesión).',
    icon: <FileText size={24} data-id="element-59" />
  }, {
    title: 'Menú Exprés',
    desc: 'Desayunos, comidas y cenas repetibles.',
    icon: <ShoppingBag size={24} data-id="element-60" />
  }, {
    title: 'Reintroducción',
    desc: 'Vuelves a comer normal con control.',
    icon: <CheckCircle size={24} data-id="element-61" />
  }, {
    title: 'Plan SOS',
    desc: "Qué hacer cuando 'hoy fue horrible'.",
    icon: <Heart size={24} data-id="element-62" />
  }];
  return <Section className="bg-[#FDF8F3]" data-id="element-63">
      <div className="text-center mb-16" data-id="element-64">
        <motion.span initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} className="text-[#E8A87C] font-bold tracking-wider uppercase text-sm" data-id="element-65">
          La Solución
        </motion.span>
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-[#2D4A3E] mt-4" data-id="element-66">
          Mapa Anticólicos + Protocolo 7 Días
        </motion.h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-67">
        {features.map((feature, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white p-6 rounded-2xl shadow-sm border border-[#F5D5C8]/30 hover:shadow-md transition-all" data-id="element-68">
            <div className="w-12 h-12 bg-[#FDF8F3] rounded-full flex items-center justify-center text-[#E8A87C] mb-4" data-id="element-69">
              {feature.icon}
            </div>
            <h3 className="text-xl font-bold text-[#2D4A3E] mb-2" data-id="element-70">
              {feature.title}
            </h3>
            <p className="text-[#2D4A3E]/70" data-id="element-71">{feature.desc}</p>
          </motion.div>)}
      </div>
    </Section>;
};
const Included = () => {
  const items = ['Ebook PDF (guía completa)', 'Menú de 7 días (Latino + USA grocery)', 'Lista de compras semana 1', 'Diario Anticólicos imprimible', 'Checklist de señales de alarma'];
  return <Section className="bg-white border-y border-[#F5D5C8]/30" data-id="element-72">
      <div className="max-w-4xl mx-auto bg-[#2D4A3E]/5 rounded-3xl p-8 md:p-12" data-id="element-73">
        <div className="text-center mb-10" data-id="element-74">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2D4A3E]" data-id="element-75">
            Lo que incluye
          </h2>
          <p className="text-[#2D4A3E]/70 mt-2" data-id="element-76">
            Todo lo que necesitas para empezar hoy mismo
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-77">
          {items.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: idx * 0.1
        }} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm" data-id="element-78">
              <div className="text-[#E8A87C]" data-id="element-79">
                <CheckCircle size={24} data-id="element-80" />
              </div>
              <span className="font-medium text-[#2D4A3E]" data-id="element-81">{item}</span>
            </motion.div>)}
        </div>
      </div>
    </Section>;
};
const Results = () => {
  return <Section className="bg-[#FDF8F3]" data-id="element-82">
      <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-83">
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} whileInView={{
        opacity: 1,
        x: 0
      }} className="space-y-8" data-id="element-84">
          <h2 className="text-4xl font-bold text-[#2D4A3E]" data-id="element-85">
            Qué cambia en 7 días
          </h2>
          <p className="text-lg text-[#2D4A3E]/80" data-id="element-86">
            No te voy a vender "cólicos cero para siempre". Pero sí resultados
            realistas:
          </p>

          <div className="space-y-6" data-id="element-87">
            {['Puedes ver menos intensidad / menos duración', 'Puedes encontrar 1–2 detonantes probables (si existen)', "Puedes dejar de improvisar y sentir: 'ok, hoy sé qué hacer'"].map((text, idx) => <div key={idx} className="flex gap-4" data-id="element-88">
                <div className="w-1 h-full min-h-[2rem] bg-[#E8A87C] rounded-full" data-id="element-89"></div>
                <p className="text-lg font-medium text-[#2D4A3E]" data-id="element-90">{text}</p>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} className="bg-[#F5D5C8] p-8 rounded-3xl relative overflow-hidden" data-id="element-91">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-[#E8A87C] rounded-full opacity-50 blur-xl" data-id="element-92"></div>
          <h3 className="text-2xl font-bold text-[#2D4A3E] mb-6 relative z-10" data-id="element-93">
            Qué NO es este ebook
          </h3>
          <ul className="space-y-4 relative z-10" data-id="element-94">
            {['No es diagnóstico médico', 'No reemplaza a tu pediatra ni IBCLC', 'No te obliga a eliminar medio mundo', 'No te pide cocinar gourmet'].map((text, idx) => <li key={idx} className="flex items-center gap-3 text-[#2D4A3E]/80" data-id="element-95">
                <XCircle size={18} className="text-[#2D4A3E]" data-id="element-96" />
                <span data-id="element-97">{text}</span>
              </li>)}
          </ul>
        </motion.div>
      </div>
    </Section>;
};
const Pricing = () => {
  return <Section id="pricing" className="bg-[#2D4A3E] text-white py-24" data-id="element-98">
      <div className="max-w-3xl mx-auto text-center" data-id="element-99">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} className="bg-[#FDF8F3] text-[#2D4A3E] rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-100">
          <div className="absolute top-0 left-0 w-full h-2 bg-[#E8A87C]" data-id="element-101"></div>

          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-102">
            Recupera el control hoy
          </h2>
          <p className="text-[#2D4A3E]/70 mb-8" data-id="element-103">
            Si estás cansada de adivinar... Si necesitas que alguien te diga
            "haz esto hoy"...
          </p>

          <div className="flex items-baseline justify-center gap-2 mb-8" data-id="element-104">
            <span className="text-6xl font-bold text-[#2D4A3E]" data-id="element-105">$6.86</span>
            <span className="text-[#2D4A3E]/50 text-xl line-through" data-id="element-106">$69</span>
          </div>

          <Button className="w-full md:w-auto text-lg px-12 py-5 mb-6 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" onClick={() => window.open('https://mamiserena.com/cart/51235373973794:1', '_blank')} data-id="element-107">
            Descarga la Guía Completa
            <ArrowRight className="ml-2" data-id="element-108" />
          </Button>

          <div className="flex items-center justify-center gap-2 text-sm text-[#2D4A3E]/60 bg-[#F5D5C8]/30 py-2 px-4 rounded-full inline-flex mx-auto" data-id="element-109">
            <ShieldCheck size={16} className="text-[#E8A87C]" data-id="element-110" />
            <span data-id="element-111">Garantía de satisfacción de 7 días</span>
          </div>
        </motion.div>
      </div>
    </Section>;
};
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  return <AnimatePresence data-id="element-112">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50" data-id="element-113" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full bg-[#FDF8F3] rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[90vh]" data-id="element-114">
            <div className="flex items-center justify-between p-6 border-b border-[#F5D5C8]" data-id="element-115">
              <h2 className="text-2xl font-bold text-[#2D4A3E]" data-id="element-116">{title}</h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F5D5C8] transition-colors text-[#2D4A3E]" data-id="element-117">
                <X size={24} data-id="element-118" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-[#2D4A3E]/80 text-sm leading-relaxed" data-id="element-119">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
// --- Privacy Policy Content ---
const PrivacyContent = () => <div className="space-y-6" data-id="element-120">
    <section data-id="element-121">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-122">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-123">
        Recopilamos información que nos proporcionas directamente cuando
        realizas una compra, incluyendo tu nombre y dirección de correo
        electrónico para la entrega del producto digital.
      </p>
    </section>
    <section data-id="element-124">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-125">
        2. Uso de la Información
      </h3>
      <p data-id="element-126">Utilizamos la información recopilada para:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1" data-id="element-127">
        <li data-id="element-128">Procesar y entregar tu compra</li>
        <li data-id="element-129">Enviarte actualizaciones importantes sobre el producto</li>
        <li data-id="element-130">Responder a tus consultas o solicitudes</li>
      </ul>
    </section>
    <section data-id="element-131">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-132">
        3. Protección de Datos
      </h3>
      <p data-id="element-133">
        Implementamos medidas de seguridad apropiadas para proteger tu
        información personal contra acceso no autorizado, alteración,
        divulgación o destrucción.
      </p>
    </section>
    <section data-id="element-134">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-135">4. Cookies</h3>
      <p data-id="element-136">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación. Puedes configurar tu navegador para rechazar cookies, aunque
        esto puede afectar algunas funcionalidades.
      </p>
    </section>
    <section data-id="element-137">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-138">5. Terceros</h3>
      <p data-id="element-139">
        No vendemos, comercializamos ni transferimos tu información personal a
        terceros, excepto proveedores de servicios de confianza que nos ayudan a
        operar nuestro sitio web y procesar pagos.
      </p>
    </section>
    <section data-id="element-140">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-141">
        6. Cambios a esta Política
      </h3>
      <p data-id="element-142">
        Nos reservamos el derecho de actualizar esta política de privacidad en
        cualquier momento. Los cambios serán efectivos inmediatamente después de
        su publicación en esta página.
      </p>
    </section>
    <p className="text-xs text-[#2D4A3E]/50 pt-4" data-id="element-143">
      Última actualización: Febrero 2026
    </p>
  </div>;
// --- Terms Content ---
const TermsContent = () => <div className="space-y-6" data-id="element-144">
    <section data-id="element-145">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-146">
        1. Aceptación de Términos
      </h3>
      <p data-id="element-147">
        Al acceder y utilizar este sitio web y/o comprar nuestros productos,
        aceptas estar sujeto a estos términos y condiciones de uso.
      </p>
    </section>
    <section data-id="element-148">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-149">
        2. Producto Digital
      </h3>
      <p data-id="element-150">
        El ebook "Alimentación Anticólicos para Mamás en Lactancia" es un
        producto digital descargable. Una vez completada la compra, recibirás
        acceso inmediato al contenido.
      </p>
    </section>
    <section data-id="element-151">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-152">3. Uso Personal</h3>
      <p data-id="element-153">
        El contenido adquirido es para uso personal únicamente. Queda prohibida
        la reproducción, distribución, modificación o reventa del material sin
        autorización expresa.
      </p>
    </section>
    <section data-id="element-154">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-155">
        4. Descargo de Responsabilidad Médica
      </h3>
      <p data-id="element-156">
        La información contenida en este ebook tiene fines educativos y no
        constituye consejo médico profesional. Siempre consulta con tu pediatra,
        médico o consultora de lactancia certificada (IBCLC) antes de realizar
        cambios en tu dieta o la alimentación de tu bebé.
      </p>
    </section>
    <section data-id="element-157">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-158">
        5. Política de Reembolso
      </h3>
      <p data-id="element-159">
        Ofrecemos una garantía de satisfacción de 7 días. Si no estás satisfecha
        con el producto, puedes solicitar un reembolso completo dentro de los 7
        días posteriores a la compra.
      </p>
    </section>
    <section data-id="element-160">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-161">
        6. Limitación de Responsabilidad
      </h3>
      <p data-id="element-162">
        No nos hacemos responsables de los resultados individuales obtenidos al
        seguir las recomendaciones del ebook. Los resultados pueden variar según
        cada persona y situación.
      </p>
    </section>
    <section data-id="element-163">
      <h3 className="font-bold text-[#2D4A3E] text-lg mb-2" data-id="element-164">
        7. Propiedad Intelectual
      </h3>
      <p data-id="element-165">
        Todo el contenido, incluyendo textos, gráficos, logos e imágenes, está
        protegido por derechos de autor y es propiedad exclusiva de Alimentación
        Anticólicos.
      </p>
    </section>
    <p className="text-xs text-[#2D4A3E]/50 pt-4" data-id="element-166">
      Última actualización: Febrero 2026
    </p>
  </div>;
const Footer = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-[#1F352C] text-[#FDF8F3]/60 py-12 px-4 text-center text-sm" data-id="element-167">
        <div className="max-w-4xl mx-auto space-y-4" data-id="element-168">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-169">
            <button onClick={() => setPrivacyOpen(true)} className="hover:text-[#E8A87C] transition-colors underline underline-offset-2" data-id="element-170">
              Política de Privacidad
            </button>
            <span className="text-[#FDF8F3]/30" data-id="element-171">|</span>
            <button onClick={() => setTermsOpen(true)} className="hover:text-[#E8A87C] transition-colors underline underline-offset-2" data-id="element-172">
              Términos y Condiciones
            </button>
          </div>
          <p data-id="element-173">
            © {new Date().getFullYear()} Alimentación Anticólicos. Todos los
            derechos reservados.
          </p>
          <p className="max-w-2xl mx-auto leading-relaxed" data-id="element-174">
            Descargo de responsabilidad: La información contenida en este ebook
            tiene fines educativos y no sustituye el consejo médico profesional.
            Siempre consulta con tu pediatra o consultora de lactancia antes de
            realizar cambios significativos en tu dieta o la de tu bebé.
          </p>
        </div>
      </footer>

      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-175">
        <PrivacyContent data-id="element-176" />
      </Modal>

      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-177">
        <TermsContent data-id="element-178" />
      </Modal>
    </>;
};
export function App() {
  return <div className="min-h-screen bg-[#FDF8F3] overflow-x-hidden font-sans" data-id="element-179">
      <Hero data-id="element-180" />
      <Problem data-id="element-181" />
      <FailedAttempts data-id="element-182" />
      <Solution data-id="element-183" />
      <Included data-id="element-184" />
      <Results data-id="element-185" />
      <Pricing data-id="element-186" />
      <Footer data-id="element-187" />
    </div>;
}